import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import '../../util/string';
import { OfficeSelectorCfgService } from "./officeSelectorCfg.service";
import { ApigeeService } from "src/app/auth/apigee/apigee.service";

class User {
    public UserId: string;
    public UserName: string;
}

class Unit {
    public UnitId: string;
    public Name: string;
    public Users: User[];
}

class Branch {
    public BranchId: string;
    public Name: string;
    public Units: Unit[];
}

class District {
    public DistrictId: string;
    public Name: string;
    public Branches: Branch[];
}

class Region {
    public RegionId: string;
    public Name: string;
    public Districts: District[];
}

class Company {
    public CompanyId: string;
    public FiscalName: string;
    public BOCompanyId: string;
    public Regions: Region[];
}

class UserDetail {
    public UserId: string;
    public UserName: string;
}

class UnitDetail {
    public UnitId: string;
    public Name: string;
    public Users: UserDetail[];
}

class BranchDetail {
    public BranchId: string;
    public Name: string;
    public Address: string;
    public Latitude: string;
    public Longitude: string;
    public Phone: string;
    public Fax: string;
    public BrandId: string;
    public AddressStreet: string;
    public AddressNumber: string;
    public ProvinceName: string;
    public CityName: string;
    public AddressZipCode: string;
    public Units: UnitDetail[];
}

class DistrictDetail {
    public DistrictId: string;
    public Name: string;
    public Branches: BranchDetail[];
}

class RegionDetail {
    public RegionId: string;
    public Name: string;
    public Districts: DistrictDetail[];
}

class CompanyDetail {
    public CompanyId: string;
    public FiscalName: string;
    public AccountingCOCompanyId: string;
    public BOCompanyId: string;
    public Regions: RegionDetail[];
}

export class Office {
    public id: string;
    public get selectName(): string { return this.id.padStart(4, '0') + ' ' + this.name.toLowerCase().capitalize() };
    public name: string;
    public unitId: string;
    public userId: string;
    public isTEA: boolean = false;
    public isUPayroll: boolean = false;
    public isFundation: boolean = false;
    public get isUserOffice(): boolean {
        if (this.userId && this.userId != "") return true;
        else return false;
    }
    public isProfessionals: boolean = false;
    public companyId: string;
    public regionId: string;
}

export class CompanyOffices {
    public id: string;
    public boCompanyId: string;
    public fiscalName: string;
    public isCollapsed: boolean;
    public offices: Office[];
}

@Injectable({
    providedIn: 'root'
})
export class OfficeSelectorService {
    private readonly BRANCH: string = "branch";
    private mOffice: Office;

    private selectedOfficeUpdatedSource: Subject<Office>;
    public readonly selectedOfficeUpdated: Observable<Office>;

    constructor(private officeSelectorCfgService: OfficeSelectorCfgService,
        private apigeeService: ApigeeService, private httpClient: HttpClient) {
        this.selectedOfficeUpdatedSource = new Subject<Office>();
        this.selectedOfficeUpdated = this.selectedOfficeUpdatedSource.asObservable();
    }

    public async getOfficeList(nif: string): Promise<CompanyOffices[]> {
        let cfg = this.officeSelectorCfgService.getCfg();
        let companies: CompanyOffices[] = [];
        let companyList = null;

        if (this.apigeeService.isTokenValid() == '') {
            try {
                companyList = await this.httpClient.post<Company[]>(cfg.listEndpoint, { fiscalDocNumber: nif }).toPromise();
                this.validateRequestAttempts(false);
            } catch (error) {
                this.validateRequestAttempts(true);
                return companies;
            }
        } else {
            this.validateRequestAttempts(true);
            return companies;
        }

        companyList.forEach(company => {
            let companyOffices = new CompanyOffices();
            companyOffices.id = company.CompanyId;
            companyOffices.boCompanyId = company.BOCompanyId;
            companyOffices.fiscalName = company.FiscalName;
            companyOffices.offices = [];

            let isTEA = companyOffices.boCompanyId == this.officeSelectorCfgService.getCfg().teaCompanyId;

            if (company.Regions) {
                company.Regions.forEach(region => {
                    if (region.Districts) {
                        region.Districts.forEach(district => {
                            if (district.Branches) {
                                district.Branches.forEach(branch => {
                                    let office = new Office();
                                    office.id = branch.BranchId;
                                    office.name = branch.Name;
                                    office.isTEA = isTEA;
                                    office.isUPayroll = office.id == cfg.uPayrollBranchId;
                                    office.isFundation = company.CompanyId == cfg.fundationCompanyId;
                                    office.regionId = region.RegionId;

                                    if (branch.Units && branch.Units.length > 0) {
                                        let unit = branch.Units[0];
                                        office.unitId = unit.UnitId;
                                        if (unit && unit.Users && unit.Users.length > 0) {
                                            office.userId = unit.Users[0].UserId;
                                        }
                                    }

                                    companyOffices.offices.push(office);
                                })
                            }
                        })
                    }
                })
            }

            if (companyOffices.offices.length > 0) {
                companyOffices.offices = companyOffices.offices.sort((a, b) => a.name.localeCompare(b.name));
                companies.push(companyOffices);
            }
        });

        return companies;
    }

    public async getOfficeDetail(officeId: string): Promise<CompanyDetail[]> {
        let cfg = this.officeSelectorCfgService.getCfg();
        let officeList = await this.httpClient.get<CompanyDetail[]>(cfg.detailEndpoint, { params: { branchId: officeId, unitChk: "false" } }).toPromise();
        return officeList;
    }

    public get selectedOffice(): Office {
        if (!this.mOffice) {
            let officeSerialized = localStorage.getItem(this.BRANCH);
            if (officeSerialized && officeSerialized !== "") {
                let parsedOffice = JSON.parse(officeSerialized);
                if (parsedOffice.isTEA === undefined) {
                    this.mOffice = null;
                } else {
                    let selectedOffice = new Office();
                    selectedOffice.id = parsedOffice.id;
                    selectedOffice.isTEA = parsedOffice.isTEA;
                    selectedOffice.isUPayroll = parsedOffice.isUPayroll;
                    selectedOffice.isFundation = parsedOffice.isFundation;
                    selectedOffice.isProfessionals = parsedOffice.isProfessionals;
                    selectedOffice.name = parsedOffice.name;
                    selectedOffice.unitId = parsedOffice.unitId;
                    selectedOffice.userId = parsedOffice.userId;
                    selectedOffice.companyId = parsedOffice.companyId;
                    selectedOffice.regionId = parsedOffice.regionId;

                    this.mOffice = selectedOffice;
                }
            } else {
                this.mOffice = null;
            }
        }

        return this.mOffice;
    }

    public async setSelectedOffice(office: Office): Promise<void> {
        let hasChanged = false;
        if (office) {
            if (this.mOffice) {
                hasChanged = office.id != this.mOffice.id;
            } else {
                hasChanged = true;
            }
        } else {
            hasChanged = true;
        }

        if (hasChanged) {
            this.mOffice = office;
            if (office) {
                let officeDetail = await this.getOfficeDetail(office.id);
                office.companyId = officeDetail[0].CompanyId;
                localStorage.setItem(this.BRANCH, JSON.stringify(office));
            }
            else localStorage.removeItem(this.BRANCH);
            this.selectedOfficeUpdatedSource.next(office);
        }
    }

    /**
    * Valida los intentos de solicitud
    * @param isError Indica si es un error o no
    */
    private validateRequestAttempts(isError: boolean){
        if(!isError){
            sessionStorage.removeItem("appAttemps");
        }
        else {
            let lsAttemps = sessionStorage.getItem("appAttemps");

            if (lsAttemps !== null ){
                let attemps = Number.parseInt(lsAttemps.toString());
                if (attemps > 3 ) {
                    throw new Error('Error en el acceso a datos del selector de oficinas');
                }
                else if (attemps <= 3) {
                    sessionStorage.setItem("appAttemps",(attemps + 1).toString());
                }
            }
            else {
                sessionStorage.setItem("appAttemps", "1");
            }
        }
    }
}
